import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  getBestSellerProducts,
  getCategories,
  getProductBySlug,
  getProducts,
  isPromotionalCategory,
} from "../services/productService";
import {
  CategorySummary,
  Product,
  ProductAvailabilityFilter,
  ProductSort,
} from "../types/store";
import {
  CatalogMutation,
  subscribeToCatalogMutations,
} from "../utils/catalogSync";
import { findCategoryNode, getCategoryDescendantIds } from "../utils/categoryTree";
import { HOME_PAGE_SECTION_TAGS } from "../utils/homepageSections";

interface ProductContextValue {
  products: Product[];
  featuredProducts: Product[];
  bestSellerProducts: Product[];
  loading: boolean;
  error: string;
  searchTerm: string;
  availableBrands: string[];
  selectedBrand: string;
  selectedCategory: string;
  selectedPromoTag: string;
  availabilityFilter: ProductAvailabilityFilter;
  minimumDiscount: number;
  priceRange: { min: number; max: number };
  sortBy: ProductSort;
  currentPage: number;
  productsPerPage: number;
  categories: CategorySummary[];
  filteredProducts: Product[];
  paginatedProducts: Product[];
  totalPages: number;
  maxCatalogPrice: number;
  setSearchTerm: (value: string) => void;
  setSelectedBrand: (value: string) => void;
  setSelectedCategory: (value: string) => void;
  setSelectedPromoTag: (value: string) => void;
  setAvailabilityFilter: (value: ProductAvailabilityFilter) => void;
  setMinimumDiscount: (value: number) => void;
  setPriceRange: (value: { min: number; max: number }) => void;
  setSortBy: (value: ProductSort) => void;
  setCurrentPage: (value: number) => void;
  resetFilters: () => void;
  refreshCatalog: () => Promise<void>;
  getProductBySlug: (slug: string) => Promise<Product | undefined>;
  getRelatedProducts: (product: Product) => Product[];
}

const ProductContext = createContext<ProductContextValue | undefined>(undefined);

export const ProductProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [bestSellerProducts, setBestSellerProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<CategorySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTermState] = useState("");
  const [selectedBrand, setSelectedBrandState] = useState("All");
  const [selectedCategory, setSelectedCategoryState] = useState("All");
  const [selectedPromoTag, setSelectedPromoTagState] = useState("All");
  const [availabilityFilter, setAvailabilityFilterState] =
    useState<ProductAvailabilityFilter>("all");
  const [minimumDiscount, setMinimumDiscountState] = useState(0);
  const [sortBy, setSortByState] = useState<ProductSort>("featured");
  const [currentPage, setCurrentPageState] = useState(1);
  const [priceRange, setPriceRangeState] = useState({ min: 0, max: 250000 });
  const productsPerPage = 8;

  const applyCatalogMutation = useCallback((mutation: CatalogMutation) => {
    if (mutation.type === "refresh") {
      return;
    }

    if (mutation.type === "delete") {
      const removeDeletedProduct = (items: Product[]) =>
        items.filter((product) => product.id !== mutation.productId);
      setProducts(removeDeletedProduct);
      setFeaturedProducts(removeDeletedProduct);
      setBestSellerProducts(removeDeletedProduct);
      return;
    }

    const upsertProduct = (items: Product[], shouldInclude = true) => {
      const withoutProduct = items.filter((product) => product.id !== mutation.product.id);
      return shouldInclude ? [mutation.product, ...withoutProduct] : withoutProduct;
    };
    setProducts((items) => upsertProduct(items));
    setFeaturedProducts((items) => upsertProduct(items, mutation.product.featured));
    setBestSellerProducts((items) => upsertProduct(items, mutation.product.bestSeller));
  }, []);

  const loadCatalog = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    setError("");
    try {
      const [productsResult, bestSellersResult, categoriesResult] = await Promise.allSettled([
        getProducts({ size: 100 }),
        getBestSellerProducts(),
        getCategories(),
      ]);

      if (productsResult.status === "rejected") {
        throw productsResult.reason;
      }

      const pagedProducts = productsResult.value;
      const bestSellers = bestSellersResult.status === "fulfilled" ? bestSellersResult.value : [];
      const categoryList = categoriesResult.status === "fulfilled" ? categoriesResult.value : [];
      setProducts(pagedProducts.content);
      setFeaturedProducts(pagedProducts.content.filter((product) => product.featured));
      setBestSellerProducts(bestSellers);
      setCategories(
        categoryList.filter((category) => !category.showInNavbar && !isPromotionalCategory(category))
      );
      const highestPrice = pagedProducts.content.reduce(
        (maxPrice, product) => Math.max(maxPrice, product.price),
        0
      );
      setPriceRangeState((current) => ({
        min: current.min,
        max: highestPrice || 250000,
      }));
    } catch {
      setError("Unable to load products right now.");
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    const handleCatalogRefresh = () => {
      void loadCatalog(true);
    };

    window.addEventListener("catalog:categories-updated", handleCatalogRefresh);
    window.addEventListener("catalog:products-updated", handleCatalogRefresh);
    return () => {
      window.removeEventListener("catalog:categories-updated", handleCatalogRefresh);
      window.removeEventListener("catalog:products-updated", handleCatalogRefresh);
    };
  }, [loadCatalog]);

  useEffect(() => {
    return subscribeToCatalogMutations((mutation) => {
      applyCatalogMutation(mutation);
      void loadCatalog(true);
    });
  }, [applyCatalogMutation, loadCatalog]);

  const maxCatalogPrice = useMemo(
    () => products.reduce((maxPrice, product) => Math.max(maxPrice, product.price), 0),
    [products]
  );

  const availableBrands = useMemo(
    () => [...new Set(products.map((product) => product.brand))].sort((left, right) => left.localeCompare(right)),
    [products]
  );

  const selectedCategoryIds = useMemo(() => {
    if (selectedCategory === "All") {
      return null;
    }
    const category = findCategoryNode(categories, selectedCategory);
    return category ? new Set(getCategoryDescendantIds(category)) : null;
  }, [categories, selectedCategory]);

  const filteredProducts = useMemo(
    () =>
      products
        .filter((product) => {
          const query = searchTerm.trim().toLowerCase();
          const matchesSearch =
            !query ||
            product.name.toLowerCase().includes(query) ||
            product.brand.toLowerCase().includes(query) ||
            product.category.toLowerCase().includes(query) ||
            product.subcategory.toLowerCase().includes(query) ||
            product.tags.some((tag) => tag.toLowerCase().includes(query));

          const matchesCategory =
            selectedCategory === "All" ||
            Boolean(selectedCategoryIds?.has(product.subcategoryId)) ||
            product.category === selectedCategory ||
            product.categorySlug === selectedCategory ||
            product.subcategory === selectedCategory ||
            product.subcategorySlug === selectedCategory;

          const matchesBrand =
            selectedBrand === "All" || product.brand === selectedBrand;

          const matchesPromo =
            selectedPromoTag === "All" ||
            product.tags.some((tag) => tag.toLowerCase() === selectedPromoTag.toLowerCase());

          const matchesPrice =
            product.price >= priceRange.min && product.price <= priceRange.max;

          const matchesAvailability =
            availabilityFilter === "all" || product.availability === availabilityFilter;

          const matchesDiscount =
            minimumDiscount <= 0 || product.discountPercentage >= minimumDiscount;

          return (
            matchesSearch &&
            matchesCategory &&
            matchesBrand &&
            matchesPromo &&
            matchesPrice &&
            matchesAvailability &&
            matchesDiscount
          );
        })
        .sort((left, right) => {
          if (sortBy === "price-low") return left.price - right.price;
          if (sortBy === "price-high") return right.price - left.price;
          if (sortBy === "newest") return Number(right.newArrival) - Number(left.newArrival);
          if (sortBy === "discount-high") {
            return right.discountPercentage - left.discountPercentage;
          }
          if (sortBy === "name-asc") return left.name.localeCompare(right.name);
          if (sortBy === "name-desc") return right.name.localeCompare(left.name);
          return Number(right.featured) - Number(left.featured);
        }),
    [
      availabilityFilter,
      minimumDiscount,
      priceRange.max,
      priceRange.min,
      products,
      searchTerm,
      selectedBrand,
      selectedCategory,
      selectedCategoryIds,
      selectedPromoTag,
      sortBy,
    ]
  );

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / productsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedProducts = filteredProducts.slice(
    (safePage - 1) * productsPerPage,
    safePage * productsPerPage
  );

  const setSearchTerm = (value: string) => {
    setSearchTermState(value);
    setCurrentPageState(1);
  };

  const setSelectedCategory = (value: string) => {
    setSelectedCategoryState(value);
    setCurrentPageState(1);
  };

  const setSelectedBrand = (value: string) => {
    setSelectedBrandState(value);
    setCurrentPageState(1);
  };

  const setSelectedPromoTag = (value: string) => {
    setSelectedPromoTagState(value);
    setCurrentPageState(1);
  };

  const setAvailabilityFilter = (value: ProductAvailabilityFilter) => {
    setAvailabilityFilterState(value);
    setCurrentPageState(1);
  };

  const setMinimumDiscount = (value: number) => {
    setMinimumDiscountState(value);
    setCurrentPageState(1);
  };

  const setPriceRange = (value: { min: number; max: number }) => {
    setPriceRangeState(value);
    setCurrentPageState(1);
  };

  const setSortBy = (value: ProductSort) => {
    setSortByState(value);
    setCurrentPageState(1);
  };

  const setCurrentPage = (value: number) => setCurrentPageState(value);

  const resetFilters = () => {
    setSearchTermState("");
    setSelectedBrandState("All");
    setSelectedCategoryState("All");
    setSelectedPromoTagState("All");
    setAvailabilityFilterState("all");
    setMinimumDiscountState(0);
    setSortByState("featured");
    setCurrentPageState(1);
    setPriceRangeState({ min: 0, max: maxCatalogPrice || 250000 });
  };

  const resolveProductBySlug = useCallback(async (slug: string) => {
    const existingProduct = products.find((product) => product.slug === slug);
    if (existingProduct) {
      return existingProduct;
    }
    try {
      return await getProductBySlug(slug);
    } catch {
      return undefined;
    }
  }, [products]);

  const getRelatedProducts = (product: Product) => {
    const normalizedProductCategory = product.category.trim().toLowerCase();
    const normalizedProductCategorySlug = product.categorySlug.trim().toLowerCase();
    const normalizedProductSubcategory = product.subcategory.trim().toLowerCase();
    const normalizedProductSubcategorySlug = product.subcategorySlug.trim().toLowerCase();
    const sectionTags = new Set(
      product.tags
        .map((tag) => tag.trim().toLowerCase())
        .filter((tag) => HOME_PAGE_SECTION_TAGS.includes(tag as (typeof HOME_PAGE_SECTION_TAGS)[number])),
    );

    const scoreProduct = (candidate: Product) => {
      const candidateTags = candidate.tags.map((tag) => tag.trim().toLowerCase());
      const sharedSectionTags = candidateTags.filter((tag) => sectionTags.has(tag)).length;
      const matchesSubcategory =
        candidate.subcategoryId === product.subcategoryId ||
        candidate.subcategory.trim().toLowerCase() === normalizedProductSubcategory ||
        candidate.subcategorySlug.trim().toLowerCase() === normalizedProductSubcategorySlug;
      const matchesCategory =
        candidate.category.trim().toLowerCase() === normalizedProductCategory ||
        candidate.categorySlug.trim().toLowerCase() === normalizedProductCategorySlug;

      return (
        sharedSectionTags * 100 +
        (matchesSubcategory ? 30 : 0) +
        (matchesCategory ? 15 : 0) +
        (candidate.featured ? 3 : 0) +
        (candidate.bestSeller ? 2 : 0) +
        (candidate.newArrival ? 1 : 0)
      );
    };

    return products
      .filter((candidate) => candidate.id !== product.id)
      .filter((candidate) => {
        const candidateCategory = candidate.category.trim().toLowerCase();
        const candidateCategorySlug = candidate.categorySlug.trim().toLowerCase();
        const candidateSubcategory = candidate.subcategory.trim().toLowerCase();
        const candidateSubcategorySlug = candidate.subcategorySlug.trim().toLowerCase();
        const sharesSectionTag = candidate.tags.some((tag) => sectionTags.has(tag.trim().toLowerCase()));

        return (
          sharesSectionTag ||
          candidate.subcategoryId === product.subcategoryId ||
          candidateSubcategory === normalizedProductSubcategory ||
          candidateSubcategorySlug === normalizedProductSubcategorySlug ||
          candidateCategory === normalizedProductCategory ||
          candidateCategorySlug === normalizedProductCategorySlug
        );
      })
      .sort((left, right) => {
        const scoreDifference = scoreProduct(right) - scoreProduct(left);
        if (scoreDifference !== 0) {
          return scoreDifference;
        }

        return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
      })
      .slice(0, 4);
  };

  return (
    <ProductContext.Provider
      value={{
        products,
        featuredProducts,
        bestSellerProducts,
        loading,
        error,
        searchTerm,
        availableBrands,
        selectedBrand,
        selectedCategory,
        selectedPromoTag,
        availabilityFilter,
        minimumDiscount,
        priceRange,
        sortBy,
        currentPage: safePage,
        productsPerPage,
        categories,
        filteredProducts,
        paginatedProducts,
        totalPages,
        maxCatalogPrice,
        setSearchTerm,
        setSelectedBrand,
        setSelectedCategory,
        setSelectedPromoTag,
        setAvailabilityFilter,
        setMinimumDiscount,
        setPriceRange,
        setSortBy,
        setCurrentPage,
        resetFilters,
        refreshCatalog: () => loadCatalog(true),
        getProductBySlug: resolveProductBySlug,
        getRelatedProducts,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error("useProducts must be used within ProductProvider");
  }
  return context;
};
